import { findCheapestTourV2Variation } from 'lib/tours/tourUtils'
import { TourV2ContentSections } from 'constants/tours'
import config from 'constants/config'
import { isEnabledForFeature } from 'lib/config/featureFlagUtils'

function determineTourV2OfferContentSections(
  offer: App.Tours.TourV2Offer,
  region: string,
  selectedVariation?: App.Tours.TourV2OfferVariation,
): Set<TourV2ContentSections> {
  const sections = new Set<TourV2ContentSections>()
  const variation = selectedVariation ?? findCheapestTourV2Variation(offer)

  if (variation?.images.length) {
    sections.add(TourV2ContentSections.GALLERY)
  }

  sections.add(TourV2ContentSections.OVERVIEW)

  if (Object.keys(offer.variations).length > 1) {
    sections.add(TourV2ContentSections.VARIATIONS)
  }

  // For each package upgrade, check how many departures have that package available
  const availablePackageUpgrades = variation?.availablePackageUpgrades ?? []

  // Build a map: packageType -> count of available departures for this variant
  const availableDepartureCountsByPackageType = new Map<string, number>()

  availablePackageUpgrades.forEach(pkg => {
    // Count number of "available" departures for this package
    const departuresWithPackage = Object.values(offer.departures)
      .filter(dep =>
        dep.fkVariationId === variation?.id &&
        dep.status === 'available' &&
        dep.options.some(opt =>
          opt.packageType === pkg.packageType,
        ),
      )
    availableDepartureCountsByPackageType.set(pkg.packageType, departuresWithPackage.length)
  })

  // If at least one package has > 0 available departures, show the section
  const preferredPackages =
    availablePackageUpgrades.filter(pkg => (availableDepartureCountsByPackageType.get(pkg.packageType) ?? 0) > 0)

  if (offer.highlightPackages === true && preferredPackages.length > 1) {
    sections.add(TourV2ContentSections.PREFERRED_PACKAGES)
  }

  if (variation?.itinerary?.length) {
    sections.add(TourV2ContentSections.ITINERARY)
  }

  if (
    variation?.travelInclusions.length ||
    variation?.diningInclusions.length
  ) {
    sections.add(TourV2ContentSections.INCLUSIONS)
  }

  if (Object.keys(offer.departures).length && offer.purchasableOptions.length) {
    sections.add(TourV2ContentSections.DEPARTURES)
  }

  if (!offer.isSignatureSeries && config.TAILOR_MADE_TOURS_ENABLED && config.TAILOR_MADE_TOURS_REGIONS && isEnabledForFeature(config.TAILOR_MADE_TOURS_REGIONS, region)) {
    sections.add(TourV2ContentSections.ADDITIONAL_WAYS)
  }

  if (offer.finePrint.sections.length) {
    sections.add(TourV2ContentSections.FINE_PRINT)
  }

  if (offer.faq?.length) {
    sections.add(TourV2ContentSections.FAQ)
  }

  if (offer.rating?.reviewsTotal && offer.rating.score) {
    sections.add(TourV2ContentSections.REVIEWS)
  }

  sections.add(TourV2ContentSections.YOU_MAY_ALSO_LIKE)

  return sections
}

export default determineTourV2OfferContentSections
